/**
 * Date range utility functions for converting presets to actual date strings
 */

export interface DateRangeResult {
  startDate?: string;
  endDate?: string;
}

export type DateRangePreset =
  | 'Today'
  | 'Yesterday'
  | 'Last 7 days'
  | 'Last 30 days'
  | 'This Month'
  | 'Custom Range';

/**
 * Sentinel prefix for custom ranges encoded as `custom:YYYY-MM-DD:YYYY-MM-DD`.
 * The date-range picker emits values in this shape when the user selects
 * specific days on the calendar — so the dates survive the plain-string
 * `onChange(value: string)` API without breaking existing consumers.
 */
export const CUSTOM_RANGE_PREFIX = 'custom:';

/** Build the canonical `custom:start:end` value used across the app. */
export const encodeCustomRange = (startISO: string, endISO: string): string =>
  `${CUSTOM_RANGE_PREFIX}${startISO}:${endISO}`;

/** Parse a custom-range value; returns null if the string is not custom-encoded. */
export const parseCustomRange = (value: string): DateRangeResult | null => {
  if (!value.startsWith(CUSTOM_RANGE_PREFIX)) return null;
  const [startDate, endDate] = value.slice(CUSTOM_RANGE_PREFIX.length).split(':');
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
};

/**
 * Convert date range preset (or a custom-encoded range) to actual date strings.
 * Returns full ISO timestamps representing local-day boundaries
 * (00:00:00.000 local → 23:59:59.999 local), so the backend matches
 * sessions against the user's calendar day, not UTC.
 */
export const getDateRangeFromPreset = (preset: string): DateRangeResult => {
  // Custom ranges take precedence — the picker emits these when the user
  // picks days on the calendar rather than a preset.
  const custom = parseCustomRange(preset);
  if (custom?.startDate && custom?.endDate) {
    return {
      startDate: localDayStartISO(custom.startDate),
      endDate: localDayEndISO(custom.endDate),
    };
  }

  const today = new Date();
  const endYmd = formatDateToString(today);

  switch (preset) {
    case 'Today': {
      return { startDate: localDayStartISO(endYmd), endDate: localDayEndISO(endYmd) };
    }
    case 'Yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const ymd = formatDateToString(yesterday);
      return { startDate: localDayStartISO(ymd), endDate: localDayEndISO(ymd) };
    }
    case 'Last 7 days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return {
        startDate: localDayStartISO(formatDateToString(start)),
        endDate: localDayEndISO(endYmd),
      };
    }
    case 'Last 30 days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return {
        startDate: localDayStartISO(formatDateToString(start)),
        endDate: localDayEndISO(endYmd),
      };
    }
    case 'This Month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: localDayStartISO(formatDateToString(start)),
        endDate: localDayEndISO(endYmd),
      };
    }
    default:
      return {};
  }
};

/**
 * Human-readable label for the DateRangePicker button.
 * Presets pass through; custom-encoded values become `Apr 1 – Apr 19` style.
 */
export const formatDateRangeLabel = (value: string): string => {
  const custom = parseCustomRange(value);
  if (!custom || !custom.startDate || !custom.endDate) return value;

  const fmt = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  return `${fmt(custom.startDate)} – ${fmt(custom.endDate)}`;
};

/**
 * Format a Date object to a local-timezone YYYY-MM-DD string.
 *
 * We deliberately AVOID `toISOString()` here — it converts to UTC, so in any
 * positive-offset timezone (e.g. Ukraine UTC+3) a date picked on the calendar
 * as "April 1 local" serializes to "March 31 UTC", shifting the whole range
 * one day earlier on the backend.
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Return a full ISO timestamp for 00:00:00.000 in the user's LOCAL timezone.
 * Example (Kyiv, UTC+2): `localDayStartISO('2026-03-01')` →
 *   `'2026-02-28T22:00:00.000Z'` (i.e. Kyiv midnight expressed in UTC).
 *
 * This ensures the backend matches sessions against a LOCAL-day window
 * rather than a UTC-day window, so "March" means the user's calendar month.
 */
export const localDayStartISO = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};

/** Like `localDayStartISO`, but for the last millisecond of the local day. */
export const localDayEndISO = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};

/**
 * Parse a YYYY-MM-DD string to Date object
 */
export const parseDateString = (dateStr: string): Date | null => {
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};
